// src/services/staffConfirmation/confirmationHandles.ts - one click, one request.
//
// Every step-up surface already threads "the code" from the operator's dialog to
// the request header (`stepUpHeaders(code)`, a literal step-up header, the prompt
// host's re-send). With "Confirm with Google" on, the dialog hands the action an
// opaque HANDLE in place of the code. The handle names a confirmation attempt
// that was started inside the operator's click (so the Google window could open)
// and is resolved at the transport (bindStaffConfirmation), which is the only
// place that knows the exact request to bind it to.
//
// A handle is single use: the first request that carries it takes the attempt,
// and any other request with the same handle is refused before it is sent. It is
// a random local id, never a credential, and it never reaches the wire: the
// binder swaps it for the confirmation, and a client without the binder would
// send it as a code the api refuses.
import { StaffConfirmationError } from "./staffConfirmationErrors";

export interface StaffConfirmRequest {
  /** hex SHA-256 of the exact request (requestHash.ts). */
  requestHash: string;
}

/**
 * The one thing a confirmation method does. The Google re-sign-in is today's
 * implementation; a staff device key could replace it without touching any
 * caller.
 */
export interface StaffConfirmProvider {
  confirm(request: StaffConfirmRequest): Promise<{ confirmation: string }>;
}

/** An attempt started in a click: it can also be abandoned (closes its window). */
export interface StaffConfirmAttempt extends StaffConfirmProvider {
  cancel(): void;
}

const PREFIX = "staff-confirmation:";
const pending = new Map<string, StaffConfirmAttempt>();
// Taken by a request and not yet released: the confirmation may be under way.
const taken = new Map<string, StaffConfirmAttempt>();
// Abandoned by the operator (the dialog closed) while it was under way.
const cancelled = new Set<string>();

const randomId = (): string => {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
};

export const isStaffConfirmationHandle = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith(PREFIX);

/** Register an attempt and get the handle a dialog passes as "the code". */
export const issueStaffConfirmationHandle = (
  attempt: StaffConfirmAttempt
): string => {
  const handle = `${PREFIX}${randomId()}`;
  pending.set(handle, attempt);
  return handle;
};

/** Take the attempt for a handle; a second take is refused. */
export const takeStaffConfirmationAttempt = (
  handle: string
): StaffConfirmAttempt => {
  const attempt = pending.get(handle);
  if (!attempt) throw new StaffConfirmationError("CONFIRMATION_HANDLE_SPENT");
  pending.delete(handle);
  taken.set(handle, attempt);
  return attempt;
};

/**
 * The dialog is done with this handle. If no request took it (the action failed
 * before sending), the attempt is cancelled so its window closes.
 */
export const releaseStaffConfirmationHandle = (handle: string): void => {
  taken.delete(handle);
  cancelled.delete(handle);
  const attempt = pending.get(handle);
  if (!attempt) return;
  pending.delete(handle);
  attempt.cancel();
};

/**
 * The operator walked away (closed the dialog or the prompt) while this handle
 * may be confirming. Its window closes, and a request still waiting on it is
 * refused before it is sent (bindStaffConfirmation checks after the confirmation
 * arrives). A request already sent cannot be recalled; this is a no-op then.
 */
export const cancelStaffConfirmationHandle = (handle: string): void => {
  const attempt = pending.get(handle) ?? taken.get(handle);
  if (!attempt) return;
  cancelled.add(handle);
  pending.delete(handle);
  attempt.cancel();
};

export const isStaffConfirmationHandleCancelled = (handle: string): boolean =>
  cancelled.has(handle);

/** Does this text carry a handle anywhere? (A handle must only ride the header.) */
export const containsStaffConfirmationHandle = (text: string): boolean =>
  text.includes(PREFIX);

/** Test seam. */
export const resetStaffConfirmationHandlesForTests = (): void => {
  pending.forEach((attempt) => attempt.cancel());
  pending.clear();
  taken.clear();
  cancelled.clear();
};
